import * as THREE from 'three';
import { Utils } from '../../shared/utils.js';

export class Profile extends THREE.Object3D {
  static counter = 0;

  points: THREE.Vector3[] = [];
  spheres: THREE.Mesh[] = [];
  edges: THREE.Line[] = [];
  boxes: THREE.Mesh[] = [];
  width = 1;
  height = 20;
  _modifiable = true;

  sphereGeometry = new THREE.SphereGeometry(0.4, 10, 10);
  color = new THREE.Color(0xff0000);
  lineColor = new THREE.Color(0xff0000);

  constructor() {
    super();

    Profile.counter++;
    this.name = 'Profile_' + Profile.counter;
  }

  createSphereMaterial(): THREE.MeshLambertMaterial {
    return new THREE.MeshLambertMaterial({
      color: 0xff0000,
      depthTest: false,
      depthWrite: false,
    });
  }

  getSegments(): { start: THREE.Vector3; end: THREE.Vector3 }[] {
    const segments: { start: THREE.Vector3; end: THREE.Vector3 }[] = [];

    for (let i = 0; i < this.points.length - 1; i++) {
      const start = this.points[i].clone();
      const end = this.points[i + 1].clone();
      segments.push({ start, end });
    }

    return segments;
  }

  getSegmentMatrices(): THREE.Matrix4[] {
    const segments = this.getSegments();
    const matrices: THREE.Matrix4[] = [];

    for (const segment of segments) {
      const { start, end } = segment;

      const box = new THREE.Object3D();
      const length = start.clone().setZ(0).distanceTo(end.clone().setZ(0));
      box.scale.set(length, 10000, this.width);
      box.up.set(0, 0, 1);

      const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      const diff = new THREE.Vector3().subVectors(end, start);
      const target = new THREE.Vector3(diff.y, -diff.x, 0);

      box.position.set(0, 0, 0);
      box.lookAt(target);
      box.position.copy(center);

      box.updateMatrixWorld();
      matrices.push(box.matrixWorld);
    }

    return matrices;
  }

  addMarker(point: THREE.Vector3): void {
    this.points.push(point);

    const sphere = new THREE.Mesh(this.sphereGeometry, this.createSphereMaterial());
    this.add(sphere);
    this.spheres.push(sphere);

    // edges & boxes
    if (this.points.length > 1) {
      const lineGeometry = new THREE.BufferGeometry();
      const positions = new Float32Array([0, 0, 0, 0, 0, 0]);
      const colors = new Float32Array([
        this.lineColor.r,
        this.lineColor.g,
        this.lineColor.b,
        this.lineColor.r,
        this.lineColor.g,
        this.lineColor.b,
      ]);
      lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      lineGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      const lineMaterial = new THREE.LineBasicMaterial({
        vertexColors: true,
        linewidth: 2,
        transparent: true,
        opacity: 0.4,
      });
      lineMaterial.depthTest = false;
      const edge = new THREE.Line(lineGeometry, lineMaterial);
      edge.visible = false;

      this.add(edge);
      this.edges.push(edge);

      const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
      const boxMaterial = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.2,
      });
      const box = new THREE.Mesh(boxGeometry, boxMaterial);
      box.visible = false;

      this.add(box);
      this.boxes.push(box);
    }

    {
      // event listeners
      const drag = (e: any) => {
        const I = Utils.getMousePointCloudIntersection(
          e.drag.end,
          e.viewer.scene.getActiveCamera(),
          e.viewer,
          e.viewer.scene.pointclouds,
        );

        if (I) {
          const i = this.spheres.indexOf(e.drag.object);
          if (i !== -1) {
            this.setPosition(i, I.location);
          }
        }
      };

      const drop = (e: any) => {
        const i = this.spheres.indexOf(e.drag.object);
        if (i !== -1) {
          this.dispatchEvent({
            type: 'marker_dropped',
            profile: this,
            index: i,
          } as any);
        }
      };

      const mouseover = (e: any) => e.object.material.emissive.setHex(0x888888);
      const mouseleave = (e: any) => e.object.material.emissive.setHex(0x000000);

      (sphere as any).addEventListener('drag', drag);
      (sphere as any).addEventListener('drop', drop);
      (sphere as any).addEventListener('mouseover', mouseover);
      (sphere as any).addEventListener('mouseleave', mouseleave);
    }

    this.dispatchEvent({
      type: 'marker_added',
      profile: this,
      sphere: sphere,
    } as any);

    this.setPosition(this.points.length - 1, point);
  }

  removeMarker(index: number): void {
    this.points.splice(index, 1);

    this.remove(this.spheres[index]);

    const edgeIndex = index === 0 ? 0 : index - 1;
    this.remove(this.edges[edgeIndex]);
    this.edges.splice(edgeIndex, 1);
    this.remove(this.boxes[edgeIndex]);
    this.boxes.splice(edgeIndex, 1);

    this.spheres.splice(index, 1);

    this.update();

    this.dispatchEvent({
      type: 'marker_removed',
      profile: this,
    } as any);
  }

  setPosition(index: number, position: THREE.Vector3): void {
    const point = this.points[index];
    point.copy(position);

    this.dispatchEvent({
      type: 'marker_moved',
      profile: this,
      index: index,
      position: point.clone(),
    } as any);

    this.update();
  }

  setWidth(width: number): void {
    this.width = width;

    this.dispatchEvent({
      type: 'width_changed',
      profile: this,
      width: width,
    } as any);

    this.update();
  }

  getWidth(): number {
    return this.width;
  }

  update(): void {
    if (this.points.length === 0) return;

    if (this.points.length === 1) {
      this.spheres[0].position.copy(this.points[0]);
      return;
    }

    const min = this.points[0].clone();
    const max = this.points[0].clone();
    const centroid = new THREE.Vector3();
    const lastIndex = this.points.length - 1;

    for (let i = 0; i <= lastIndex; i++) {
      const point = this.points[i];
      const sphere = this.spheres[i];
      const leftIndex = i === 0 ? lastIndex : i - 1;
      const leftVertex = this.points[leftIndex];
      const leftEdge = this.edges[leftIndex];
      const rightEdge = this.edges[i];
      const leftBox = this.boxes[leftIndex];

      sphere.position.copy(point);

      if (this._modifiable) {
        sphere.visible = true;
      } else {
        sphere.visible = false;
      }

      if (leftEdge) {
        const positions = (leftEdge.geometry as THREE.BufferGeometry).attributes['position']
          .array as Float32Array;
        positions[0] = leftVertex.x;
        positions[1] = leftVertex.y;
        positions[2] = leftVertex.z;
        positions[3] = point.x;
        positions[4] = point.y;
        positions[5] = point.z;
        (leftEdge.geometry as THREE.BufferGeometry).attributes['position'].needsUpdate = true;
        leftEdge.geometry.computeBoundingSphere();
      }

      if (rightEdge) {
        const positions = (rightEdge.geometry as THREE.BufferGeometry).attributes['position']
          .array as Float32Array;
        positions[0] = point.x;
        positions[1] = point.y;
        positions[2] = point.z;
        (rightEdge.geometry as THREE.BufferGeometry).attributes['position'].needsUpdate = true;
        rightEdge.geometry.computeBoundingSphere();
      }

      if (leftBox) {
        const start = leftVertex;
        const end = point;
        const length = start.clone().setZ(0).distanceTo(end.clone().setZ(0));
        leftBox.scale.set(length, 1000000, this.width);
        leftBox.up.set(0, 0, 1);

        const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        const diff = new THREE.Vector3().subVectors(end, start);
        const target = new THREE.Vector3(diff.y, -diff.x, 0);

        leftBox.position.set(0, 0, 0);
        leftBox.lookAt(target);
        leftBox.position.copy(center);
      }

      centroid.add(point);
      min.min(point);
      max.max(point);
    }
    centroid.multiplyScalar(1 / this.points.length);

    for (let i = 0; i < this.boxes.length; i++) {
      const box = this.boxes[i];
      box.position.z = min.z + (max.z - min.z) / 2;
    }
  }

  raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
    for (let i = 0; i < this.points.length; i++) {
      const sphere = this.spheres[i];
      sphere.raycast(raycaster, intersects);
    }

    for (let i = 0; i < intersects.length; i++) {
      const I = intersects[i];
      I.distance = raycaster.ray.origin.distanceTo(I.point);
    }
    intersects.sort((a, b) => a.distance - b.distance);
  }

  get modifiable(): boolean {
    return this._modifiable;
  }
  set modifiable(value: boolean) {
    this._modifiable = value;
    this.update();
  }
}
